import Utils from '@/api/Utils';
import Consts from '@/api/Consts';

import type {
	BaseStationDisplay,
	BaseStationDisplaySource,
	ConnectedBaseStation,
	VRSParticipantRoverFeatureData,
} from '@/components/map/types/mapFeatureTypes';

import type {
	BaseStation,
	BaseStationLiveStatusKey,
	BaseStationOnlineStatusKey,
	BaseStationStatusKey,
	CoordsXYZItem,
	CoordsBLItem,
	GetterUserAttempt,
	ParsedGetterAdditionalData,
	ConnectedBaseStationRaw,
	VRSParticipantRoverRaw,
	StatusKey,
	StatusVisual,
	StatusVisualWithKey,
} from '@/utils/types/baseStationsTypes';


const STATUS_DISPLAY: Record<StatusKey, StatusVisual> = {
	updating: {
		badge: 'processing',
		action: 'processing',
	},
	working: {
		badge: 'success',
		action: 'stop',
	},
	stopped: {
		badge: 'default',
		action: 'start',
	},
	failed: {
		badge: 'error',
		action: 'start',
	},
	deleted: {
		badge: 'default',
		action: 'deleted',
	},

	// online
	no_data: {
		badge: 'processing',
		action: 'stop',
	},
	online: {
		badge: 'success',
		action: 'stop',
	},
	some_breaks: {
		badge: 'warning',
		action: 'stop',
	},
	broken: {
		badge: 'error',
		action: 'stop',
	},
	offline: {
		badge: 'error',
		action: 'start',
	},
	// чисто для легенды
	offlineInLegend: {
		badge: 'default',
		action: 'start',
	},
	// see also getBaselineColor
	no_fix: {
		badge: 'processing',
		action: 'stop',
	},
	fixed: {
		badge: 'success',
		action: 'stop',
	},
	differential: {
		badge: 'success',
		action: 'stop',
	},
	not_applicable: {
		badge: 'processing',
		action: 'stop',
	},
	rtk_fixed: {
		badge: 'success',
		action: 'stop',
	},
	rtk_float: {
		badge: 'success',
		action: 'stop',
	},
	dead_reckoning: {
		badge: 'success',
		action: 'stop',
	},
	restarting: {
		badge: 'processing',
		action: 'stop',
	},

	// live data
	live_data: {
		badge: 'success',
		action: 'stop',
	},
	no_live_data: {
		badge: 'error',
		action: 'stop',
	},

	// vrs accuracy
	no_traffic: {
		badge: 'error',
		action: 'no_action',
	},
	has_traffic: {
		badge: 'success',
		action: 'no_action',
	},
	traffic_no_solution: {
		badge: 'brown',
		color: Consts.COLORS.brown,
		action: 'no_action',
	},
	in_solution: {
		badge: 'success',
		action: 'no_action',
	},

	// fallback for any unrecognised key coming from the server
	unknown: {
		badge: 'default',
		action: 'processing',
	},
};


const BASE_STATION_STATUS_KEYS: ReadonlySet<BaseStationStatusKey> = new Set([
	'updating',
	'working',
	'stopped',
	'failed',
	'deleted',
	'restarting',
	'unknown',
]);

const BASE_STATION_ONLINE_STATUS_KEYS: ReadonlySet<BaseStationOnlineStatusKey> = new Set([
	'no_data',
	'online',
	'some_breaks',
	'broken',
	'offline',
	'offlineInLegend',
	'no_fix',
	'fixed',
	'differential',
	'not_applicable',
	'rtk_fixed',
	'rtk_float',
	'dead_reckoning',
	'unknown',
]);

const BASE_STATION_LIVE_STATUS_KEYS: ReadonlySet<BaseStationLiveStatusKey> = new Set([
	'live_data',
	'no_live_data',
	'unknown',
]);


const normalizeBaseStationStatus = (raw: unknown): BaseStationStatusKey => {
	if (typeof raw === 'string' && (BASE_STATION_STATUS_KEYS as ReadonlySet<string>).has(raw)) {
		return raw as BaseStationStatusKey;
	}
	// eslint-disable-next-line no-console
	console.warn('Unknown base station status from server: ' + String(raw));
	return 'unknown';
};

const normalizeBaseStationOnlineStatus = (raw: unknown): BaseStationOnlineStatusKey => {
	if (typeof raw === 'string' && (BASE_STATION_ONLINE_STATUS_KEYS as ReadonlySet<string>).has(raw)) {
		return raw as BaseStationOnlineStatusKey;
	}
	// eslint-disable-next-line no-console
	console.warn('Unknown base station online status from server: ' + String(raw));
	return 'unknown';
};

const normalizeBaseStationLiveStatus = (raw: unknown): BaseStationLiveStatusKey => {
	if (typeof raw === 'string' && (BASE_STATION_LIVE_STATUS_KEYS as ReadonlySet<string>).has(raw)) {
		return raw as BaseStationLiveStatusKey;
	}
	// eslint-disable-next-line no-console
	console.warn('Unknown base station live status from server: ' + String(raw));
	return 'unknown';
};

const addStatusDisplayData = (keys: StatusKey[]): StatusVisualWithKey[] => keys.map(key => ({
	key,
	...STATUS_DISPLAY[key],
}));

const BASE_STATION_LEGEND_ITEMS = addStatusDisplayData(['online', 'some_breaks', 'broken', 'no_data', 'stopped', 'offline']);
const BASELINE_LEGEND_ITEMS = addStatusDisplayData(['online', 'fixed', 'differential', 'rtk_fixed', 'rtk_float', 'dead_reckoning', 'no_data', 'no_fix', 'updating', 'offlineInLegend', 'broken']);
const VRS_ACCURACY_LEGEND_ITEMS = addStatusDisplayData(['no_traffic', 'traffic_no_solution', 'in_solution']);

const getStatusId = (item: BaseStation): StatusKey => {
	if (item.deleted) {
		return 'deleted';
	}
	if (item.updating) {
		return 'updating';
	}
	return item.status;
};

const getStatusOnlineId = (item: BaseStation): StatusKey => {
	const statusId = getStatusId(item);
	if (statusId === 'working') {
		return item.status_online;
	}
	return statusId;
};

const getStatusLiveId = (item: BaseStation): StatusKey => {
	const statusId = getStatusId(item);
	if (statusId === 'working') {
		return item.status_live;
	}
	return statusId;
};


const getStatusVisualProps = (itemId: StatusKey): StatusVisualWithKey => ({
	...STATUS_DISPLAY[itemId],
	key: itemId,
});


const getMountpointDisplay = (item: BaseStationDisplaySource): string | null | undefined => {
	if (item.mntpnt_int) {
		return item.mntpnt_int;
	}
	if ('mntpnt_src' in item) {
		return item.mntpnt_src;
	}
	return undefined;
};


const addBaseStationStatusData = (item: BaseStationDisplaySource): BaseStationDisplay => ({
	...item,
	key: item.id,
	mountpointDisplay: getMountpointDisplay(item),
	statusData: getStatusVisualProps(getStatusId(item)),
	statusOnlineData: getStatusVisualProps(getStatusOnlineId(item)),
	statusLiveData: getStatusVisualProps(getStatusLiveId(item)),
});


const COORD_XYZ_FIELDS = ['xCoord', 'yCoord', 'zCoord'] as const satisfies readonly (keyof CoordsXYZItem)[];

const addCoords = <T extends CoordsXYZItem>(item: T): T => {
	const result = { ...item };
	for (const field of COORD_XYZ_FIELDS) {
		const value = result[field];
		if (value != null) {
			result[field] = Utils.mm2m(Number(value));
		}
	}
	return result;
};


const withCoords = (item: CoordsBLItem): boolean => (item.b_coord != null && item.l_coord != null);

// TODO (D7): tighten additional_data schema once the runtime format stabilizes.
type ParsedAdditionalDataRaw = {
	users?: Record<string, number>;
};

const isParsedAdditionalDataRaw = (value: unknown): value is ParsedAdditionalDataRaw => (
	typeof value === 'object' && value !== null
);

const parseGetterAdditionalData = (additionalData: string | null | undefined): ParsedGetterAdditionalData => {
	if (!additionalData) {
		return {};
	}
	let parsedAdditionalData: unknown;
	try {
		parsedAdditionalData = JSON.parse(additionalData);
	} catch (e) {
		return {};
	}
	if (!isParsedAdditionalDataRaw(parsedAdditionalData)) {
		return {};
	}
	const result: ParsedGetterAdditionalData = { ...parsedAdditionalData };
	if (parsedAdditionalData.users) {
		const userAttempts = Object.entries(parsedAdditionalData.users);
		userAttempts.sort();
		result.userAttempts = userAttempts.map(([username, attempts]): GetterUserAttempt => ({
			username,
			attempts,
		}));
	}
	return result;
};

const parseConnectedBaseStation = (item: ConnectedBaseStationRaw | null): ConnectedBaseStation | null => {
	if (item == null) {
		return null;
	}
	return {
		...item,
		lat: item.b_coord,
		lon: item.l_coord,
	};
};


const addGetterData = (
	item: VRSParticipantRoverRaw,
	connectedBaseStation?: ConnectedBaseStation | null,
): VRSParticipantRoverFeatureData => ({
	...item,
	...parseGetterAdditionalData(item.additional_data),
	connectedBaseStation: connectedBaseStation !== undefined
		? connectedBaseStation
		: parseConnectedBaseStation(item.connected_base_station),
});


const hasCoords = (baseStationObj: CoordsXYZItem): boolean => {
	for (const field of ['xCoord', 'yCoord', 'zCoord'] as const) {
		if (Utils.safeTrim(baseStationObj[field], '').length == 0) {
			return false;
		}
	}
	return true;
};


export {
	addBaseStationStatusData,
	getStatusVisualProps,
	getStatusOnlineId,
	normalizeBaseStationStatus,
	normalizeBaseStationOnlineStatus,
	normalizeBaseStationLiveStatus,
	addCoords,
	withCoords,
	parseConnectedBaseStation,
	addGetterData,
	hasCoords,
	BASE_STATION_LEGEND_ITEMS,
	BASELINE_LEGEND_ITEMS,
	VRS_ACCURACY_LEGEND_ITEMS,
};
